import { Sprite, Assets, Texture } from 'pixi.js';
import type { ISprite, SpriteOptions, SpriteTweenOpts, VfxBlendMode } from './IRenderer';
import type { HitEllipseSprite } from './npc/NpcHoverGlow';
import { PixiOverlayHost } from './PixiOverlayHost';
import { Tween, Easing, type EasingFn } from './Tween';

const EASE_MAP: Record<NonNullable<SpriteTweenOpts['ease']>, EasingFn> = {
  linear: Easing.linear,
  sineInOut: Easing.sineInOut,
  sineOut: Easing.sineOut,
  cubicOut: Easing.cubicOut,
  power2: Easing.power2,
  backIn: Easing.backIn,
};

// Module-level cache for textures keyed by dataUrl. Pixi v8 caches by URL
// internally inside Assets, but having an explicit map lets us answer
// hasTexture(key) synchronously after a successful loadTexture.
const textureCache = new Map<string, Texture>();

type TextureWithOriginalSize = { orig?: { width?: number; height?: number } };

/** Pixi's width/height setters divide by the texture's original dimensions. */
export function hasUsableTextureDimensions(texture: TextureWithOriginalSize | null | undefined): boolean {
  const width = texture?.orig?.width;
  const height = texture?.orig?.height;
  return Number.isFinite(width) && Number.isFinite(height) && (width ?? 0) > 0 && (height ?? 0) > 0;
}

/**
 * Register a SHARED texture by key — usable by any PixiSprite via
 * setTexture(key) / initialTextureKey. Unlike loadTexture (instance-scoped),
 * this is for renderer-owned textures preloaded at boot (glow gradients,
 * atmosphere ribbons — VFX evolution arc). Idempotent + dedup'd.
 */
export async function registerSharedTexture(key: string, dataUrl: string): Promise<void> {
  if (textureCache.has(key)) return;
  const texture = await Assets.load<Texture>({ src: dataUrl, alias: key });
  textureCache.set(key, texture);
}

/** Register a SHARED texture straight from a canvas — no PNG encode/decode
 *  round trip (glow textures; lane AL: toDataURL of the six glow canvases cost
 *  ~140 ms of main thread on every page load, and portals reload the page). */
export function registerCanvasTexture(key: string, canvas: HTMLCanvasElement): void {
  if (textureCache.has(key)) return;
  textureCache.set(key, Texture.from(canvas));
}

/** Register a pre-baked RenderTexture under a key (used by bakeGraphicsToSprite).
 *  Unlike registerSharedTexture (which loads from a dataUrl), this takes a
 *  Texture directly — the bake path generates it via renderer.generateTexture(). */
export function registerBakedTexture(key: string, texture: Texture): void {
  if (textureCache.has(key)) return; // dedupe — same key = same texture
  textureCache.set(key, texture);
}

/** Whether a shared/loaded texture key is available synchronously. */
export function hasSharedTexture(key: string): boolean {
  return textureCache.has(key);
}

/** The shared texture for a key, or undefined if not registered yet. */
export function getSharedTexture(key: string): Texture | undefined {
  return textureCache.get(key);
}

/**
 * PixiSprite — ISprite impl backed by a PIXI.Sprite on the overlay's
 * worldContainer. Texture loading is async (Assets.load); position +
 * scale + alpha + tint apply synchronously after the underlying Sprite
 * is built.
 *
 * Lazy-build: ensureReady() resolves before the underlying PIXI.Sprite
 * exists. Mutations queued in pendingState are applied once the Sprite
 * is born, mirroring the buffer pattern in PixiBarLabel + PixiNameTag.
 *
 * Animation rides through the Tween primitive driven by PixiOverlayHost's
 * tickListeners. Each call to tween() registers a Tween + a tick listener;
 * on destroy or killAllTweens we drop them all.
 */
export class PixiSprite implements ISprite, HitEllipseSprite {
  private inner: Sprite | null = null;
  private destroyed = false;
  private depth: number;
  // Optional per-layer container (Scene FX v3 M1). When set, build() adds the
  // inner sprite here instead of PixiOverlayHost.worldContainer.
  private pendingParent: { addChild(child: unknown): void } | null = null;

  // Pending state — applied to inner once it's built.
  private pendingX: number;
  private pendingY: number;
  private pendingDisplayWidth: number;
  private pendingDisplayHeight: number;
  private pendingAlpha: number;
  private pendingFlipX = false;
  private pendingAngle = 0;
  private pendingScaleX = 1;
  private pendingScaleY = 1;
  private pendingTint: number | null = null;
  private pendingBlendMode: VfxBlendMode = 'normal';
  private pendingVisible = true;
  private pendingTextureKey: string | null = null;
  private pendingOriginX = 0.5;
  private pendingOriginY = 0.5;
  // Interactivity must buffer like every other pending field: renderers wire
  // click-to-target synchronously at construction (MobRenderer/NPCRenderer),
  // which is ALWAYS before the async build() — the old if(inner)-only
  // versions silently dropped the wiring, leaving every mob/NPC unclickable
  // (2026-06-11 probe: eventMode stayed 'passive', 0 listeners on live PTR).
  private pendingInteractive: boolean | null = null;
  private pendingPointerListeners: Array<['pointerdown' | 'pointerover' | 'pointerout', (event?: { button?: number }) => void]> = [];
  private hitEllipse: { cx: number; cy: number; rx: number; ry: number } | null = null;

  // Active tweens — each entry has a kill() and a tick-listener unsubscriber.
  private activeTweens: Array<{ tween: Tween; unsub: () => void }> = [];

  constructor(opts: SpriteOptions) {
    const { x, y, initialTextureKey, width, height, depth = 10, alpha = 1, parent } = opts;
    this.depth = depth;
    this.pendingParent = parent ?? null;
    this.pendingX = x;
    this.pendingY = y;
    this.pendingDisplayWidth = width;
    this.pendingDisplayHeight = height;
    this.pendingAlpha = alpha;
    this.pendingTextureKey = initialTextureKey;

    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      this.build();
    });
  }

  private build(): void {
    // Resolve initial texture if we have one cached. Otherwise the sprite
    // starts with the default empty Texture and a later setTexture /
    // loadTexture call updates it.
    const initialTexture = (this.pendingTextureKey && textureCache.get(this.pendingTextureKey)) || Texture.EMPTY;
    const sprite = new Sprite(initialTexture);
    sprite.anchor.set(this.pendingOriginX, this.pendingOriginY);
    sprite.x = this.pendingX;
    sprite.y = this.pendingY;
    sprite.alpha = this.pendingAlpha;
    sprite.scale.set(this.pendingScaleX * (this.pendingFlipX ? -1 : 1), this.pendingScaleY);
    sprite.angle = this.pendingAngle;
    sprite.visible = this.pendingVisible;
    sprite.zIndex = this.depth;
    if (this.pendingTint !== null) sprite.tint = this.pendingTint;
    if (this.pendingBlendMode !== 'normal') sprite.blendMode = this.pendingBlendMode;

    // Only apply width/height if the texture has real dimensions. Pixi v8
    // computes scale from texture.orig.width; setting width on a 0×0
    // EMPTY texture yields Infinity scale that survives the texture swap.
    // setTexture / loadTexture re-apply the pending size once the real
    // texture lands.
    this.applyPendingDisplaySize(sprite);

    // Apply buffered interactivity (wired pre-build by MobRenderer/NPCRenderer).
    if (this.pendingInteractive !== null) {
      sprite.eventMode = this.pendingInteractive ? 'static' : 'none';
      sprite.cursor = this.pendingInteractive ? 'pointer' : 'default';
    }
    for (const [event, callback] of this.pendingPointerListeners) {
      sprite.on(event, callback);
    }
    this.pendingPointerListeners = [];
    if (this.hitEllipse) this.applyHitEllipse(sprite);

    if (this.pendingParent) this.pendingParent.addChild(sprite);
    else PixiOverlayHost.addWorldChild(sprite);
    this.inner = sprite;
  }

  get x(): number { return this.inner ? this.inner.x : this.pendingX; }
  get y(): number { return this.inner ? this.inner.y : this.pendingY; }

  get scaleX(): number {
    if (this.inner) {
      // Account for flip-encoded negative scale.
      return Math.abs(this.inner.scale.x);
    }
    return this.pendingScaleX;
  }

  get scaleY(): number {
    return this.inner ? this.inner.scale.y : this.pendingScaleY;
  }

  setPosition(x: number, y: number): void {
    this.pendingX = x;
    this.pendingY = y;
    if (this.inner) {
      this.inner.x = x;
      this.inner.y = y;
    }
  }

  setDisplaySize(width: number, height: number): void {
    if (Number.isFinite(width)) this.pendingDisplayWidth = Math.max(0, width);
    if (Number.isFinite(height)) this.pendingDisplayHeight = Math.max(0, height);
    // The Sprite may already exist while its async texture is still EMPTY.
    // Pixi derives scale from texture.orig here, so assigning a display size
    // against 0x0 produces Infinity and can launch the next animated frame far
    // outside the viewport. Keep the authored size pending until a real
    // texture lands; setTexture/loadTexture re-apply it then.
    if (this.inner) this.applyPendingDisplaySize(this.inner);
  }

  setFlipX(flip: boolean): void {
    this.pendingFlipX = flip;
    if (this.inner) {
      const absSx = Math.abs(this.inner.scale.x);
      const safeSx = Number.isFinite(absSx) && absSx > 0 ? absSx : this.safePendingScaleX();
      this.inner.scale.x = flip ? -safeSx : safeSx;
    }
  }

  setAngle(degrees: number): void {
    this.pendingAngle = degrees;
    if (this.inner) this.inner.angle = degrees;
  }

  setScale(sx: number, sy?: number): void {
    const safeX = Number.isFinite(sx) ? sx : 1;
    const requestedY = sy ?? safeX;
    const safeY = Number.isFinite(requestedY) ? requestedY : safeX;
    const finalSx = this.pendingFlipX ? -safeX : safeX;
    this.pendingScaleX = safeX;
    this.pendingScaleY = safeY;
    if (this.inner) {
      this.inner.scale.x = finalSx;
      this.inner.scale.y = safeY;
    }
  }

  setOrigin(originX: number, originY?: number): void {
    this.pendingOriginX = originX;
    this.pendingOriginY = originY ?? originX;
    if (this.inner) this.inner.anchor.set(this.pendingOriginX, this.pendingOriginY);
  }

  setVisible(visible: boolean): void {
    this.pendingVisible = visible;
    if (this.inner) this.inner.visible = visible;
  }

  setAlpha(alpha: number): void {
    this.pendingAlpha = alpha;
    if (this.inner) this.inner.alpha = alpha;
  }

  setTint(color: number): void {
    this.pendingTint = color;
    if (this.inner) this.inner.tint = color;
  }

  clearTint(): void {
    this.pendingTint = null;
    if (this.inner) this.inner.tint = 0xffffff;
  }

  setBlendMode(mode: VfxBlendMode): void {
    this.pendingBlendMode = mode;
    if (this.inner) this.inner.blendMode = mode;
  }

  setDepth(depth: number): void {
    this.depth = depth;
    if (this.inner) this.inner.zIndex = depth;
  }

  setInteractive(enabled: boolean): void {
    this.pendingInteractive = enabled;
    if (this.inner) {
      this.inner.eventMode = enabled ? 'static' : 'none';
      this.inner.cursor = enabled ? 'pointer' : 'default';
    }
  }

  setHitEllipse(cx: number, cy: number, rx: number, ry: number): void {
    this.hitEllipse = { cx, cy, rx, ry };
    if (this.inner) this.applyHitEllipse(this.inner);
  }

  /** Local-space test that reads the live texture size, so texture swaps keep the shape. */
  private applyHitEllipse(sprite: Sprite): void {
    const e = this.hitEllipse!;
    sprite.hitArea = {
      contains: (x: number, y: number) => {
        const w = sprite.texture.orig.width || 1;
        const h = sprite.texture.orig.height || 1;
        const dx = (x / w + sprite.anchor.x - e.cx) / e.rx;
        const dy = (y / h + sprite.anchor.y - e.cy) / e.ry;
        return dx * dx + dy * dy <= 1;
      },
    };
  }

  onPointerDown(callback: (event?: { button?: number }) => void): void {
    // Pixi passes its FederatedPointerEvent, whose `button` is 2 for a right click.
    if (this.inner) this.inner.on('pointerdown', callback);
    else this.pendingPointerListeners.push(['pointerdown', callback]);
  }

  onPointerOver(callback: () => void): void {
    if (this.inner) this.inner.on('pointerover', callback);
    else this.pendingPointerListeners.push(['pointerover', callback]);
  }

  onPointerOut(callback: () => void): void {
    if (this.inner) this.inner.on('pointerout', callback);
    else this.pendingPointerListeners.push(['pointerout', callback]);
  }

  getBounds(): { x: number; y: number; width: number; height: number } {
    // While the inner Pixi sprite is still loading (or hasn't been built yet),
    // callers like CombatSystem's touch-damage / click-target checks need a
    // stable boundary based on the authored size/position. Fall back to the
    // pending display bounds (top-left derived from anchor/origin) so newly
    // spawned mobs/players don't report a 0×0 hitbox during the first frames.
    if (!this.inner || this.inner.texture === Texture.EMPTY) {
      return {
        x: this.pendingX - this.pendingDisplayWidth * this.pendingOriginX,
        y: this.pendingY - this.pendingDisplayHeight * this.pendingOriginY,
        width: this.pendingDisplayWidth,
        height: this.pendingDisplayHeight,
      };
    }
    const b = this.inner.getBounds();
    return { x: b.x, y: b.y, width: b.width, height: b.height };
  }

  hasTexture(key: string): boolean {
    return textureCache.has(key);
  }

  setTexture(key: string): boolean {
    const tex = textureCache.get(key);
    if (!tex) {
      this.pendingTextureKey = key;
      return false;
    }
    this.pendingTextureKey = key;
    if (this.inner) {
      this.inner.texture = tex;
      // Re-apply pending display size against the new texture's natural
      // dimensions. Pixi v8's `sprite.width` setter computes scale from
      // texture.orig.width; if the texture was previously EMPTY (0×0),
      // the scale was Infinity/NaN and that survives the swap unless we
      // re-assign the size here.
      this.applyPendingDisplaySize(this.inner);
    }
    return true;
  }

  async loadTexture(key: string, dataUrl: string): Promise<void> {
    // Pixi v8 Assets.load accepts dataUrls (incl. SVG dataUrls) and
    // dedupes internally; registerSharedTexture keeps our Map<key, Texture>
    // for synchronous hasTexture lookups.
    await registerSharedTexture(key, dataUrl);
    const texture = textureCache.get(key);
    // If we're the still-pending texture, apply now AND re-size
    // (see setTexture for the EMPTY-texture scale bug rationale).
    if (texture && this.pendingTextureKey === key && this.inner) {
      this.inner.texture = texture;
      this.applyPendingDisplaySize(this.inner);
    }
  }

  /**
   * Apply width/height only against a settled texture. A transient 0px SVG
   * frame otherwise makes Pixi derive Infinity scale, producing the tall
   * floor-to-ceiling mob columns seen during slime/pig animation swaps.
   */
  private applyPendingDisplaySize(sprite: Sprite): boolean {
    if (!hasUsableTextureDimensions(sprite.texture)) {
      // Remove any Infinity/NaN inherited from an earlier bad texture swap.
      const sx = this.safePendingScaleX();
      const sy = Number.isFinite(this.pendingScaleY) ? this.pendingScaleY : 1;
      sprite.scale.set(this.pendingFlipX ? -sx : sx, sy);
      return false;
    }
    sprite.width = this.pendingDisplayWidth;
    sprite.height = this.pendingDisplayHeight;
    return true;
  }

  private safePendingScaleX(): number {
    return Number.isFinite(this.pendingScaleX) && this.pendingScaleX > 0 ? Math.abs(this.pendingScaleX) : 1;
  }

  tween(toProps: Record<string, number>, opts: SpriteTweenOpts): void {
    // Resolve target object: tweens want to mutate a plain mutable map.
    // We back this with a proxy object that pushes writes through to the
    // sprite's getters/setters. Building the proxy lazily avoids the
    // overhead for sprites that never tween.
    if (this.destroyed) return;

    const target = this.makeTweenProxy();
    if (!target) {
      // Inner sprite isn't built yet — defer the tween until it is.
      void PixiOverlayHost.ensureReady().then(() => {
        if (this.destroyed) return;
        this.tween(toProps, opts);
      });
      return;
    }

    const tw = new Tween(target, {
      to: toProps,
      durationMs: opts.durationMs,
      ease: EASE_MAP[opts.ease ?? 'linear'],
      yoyo: opts.yoyo,
      repeat: opts.repeat,
      onComplete: () => {
        // Auto-cleanup our tracking entry.
        const idx = this.activeTweens.findIndex(e => e.tween === tw);
        if (idx >= 0) {
          this.activeTweens[idx].unsub();
          this.activeTweens.splice(idx, 1);
        }
        opts.onComplete?.();
      },
    });

    const unsub = PixiOverlayHost.addTickListener((dt) => {
      if (!tw.isAlive()) return;
      tw.step(dt);
    });

    this.activeTweens.push({ tween: tw, unsub });
  }

  killAllTweens(): void {
    for (const { tween, unsub } of this.activeTweens) {
      tween.kill();
      unsub();
    }
    this.activeTweens = [];
  }

  isActive(): boolean {
    return !this.destroyed && this.inner !== null;
  }

  destroy(): void {
    this.destroyed = true;
    this.killAllTweens();
    if (this.inner) {
      this.inner.destroy();
      this.inner = null;
    }
  }

  /**
   * Returns a writable proxy that reflects scaleX / scaleY / alpha / x / y
   * back into the underlying Sprite. Tween mutates the proxy each step;
   * the Sprite updates on every read. Returns null if the Sprite isn't
   * built yet — caller should defer.
   */
  private makeTweenProxy(): Record<string, number> | null {
    if (!this.inner) return null;
    const sprite = this.inner;
    const flipFactor = this.pendingFlipX ? -1 : 1;
    return {
      get x() { return sprite.x; },
      set x(v) { sprite.x = v; },
      get y() { return sprite.y; },
      set y(v) { sprite.y = v; },
      get alpha() { return sprite.alpha; },
      set alpha(v) { sprite.alpha = v; },
      get scaleX() { return Math.abs(sprite.scale.x); },
      set scaleX(v) { sprite.scale.x = v * flipFactor; },
      get scaleY() { return sprite.scale.y; },
      set scaleY(v) { sprite.scale.y = v; },
    } as unknown as Record<string, number>;
  }
}
